import { z } from "zod";

/**
 * Contrato da Central: visao da semana, fila do dia e detalhe da conta.
 *
 * Dinheiro em centavos inteiros. Rotulos ja chegam formatados.
 */

/** Escopos da Central. So o papel admin (equipe Monte) recebe os dois. */
export const ESCOPOS_DE_CENTRAL = {
  ler: "central:ler",
  escrever: "central:escrever",
} as const;

const centavos = z.number().int().nonnegative();
const inteiro = z.number().int().nonnegative();

export const situacaoSchema = z.enum(["ok", "aviso-precoce", "cronico", "urgente", "pouco-volume"]);
export type Situacao = z.infer<typeof situacaoSchema>;

export const statusDoAlertaSchema = z.enum(["aberto", "visto", "resolvido"]);
export type StatusDoAlerta = z.infer<typeof statusDoAlertaSchema>;

export const plataformaSchema = z.enum(["meta", "google", "kommo"]);
export type Plataforma = z.infer<typeof plataformaSchema>;

export const resumoDaSemanaSchema = z.object({
  emJogoCentavos: centavos,
  guardrailsAbertos: inteiro,
  urgentes: inteiro,
  cronicos: inteiro,
  avisos: inteiro,
  semVolume: inteiro,
  contasMonitoradas: inteiro,
  contasSemAlerta: inteiro,
});
export type ResumoDaSemana = z.infer<typeof resumoDaSemanaSchema>;

export const itemAgirHojeSchema = z.object({
  id: z.string(),
  cliente: z.string(),
  plataforma: z.enum(["meta", "google"]),
  titulo: z.string(),
  descricao: z.string(),
  acao: z.string(),
});
export type ItemAgirHoje = z.infer<typeof itemAgirHojeSchema>;

/** Uma linha por conta de anuncio + tipo de campanha. `campanha` e a de maior gasto do grupo, a que o detalhe abre. */
export const linhaDaFilaSchema = z.object({
  cliente: z.string(),
  campanha: z.string(),
  tipo: z.string().default(""),
  /** Código do tipo; a tela de detalhe usa na query para não misturar abas. */
  tipoCodigo: z.string().optional(),
  campanhas: z.number().int().positive().default(1),
  metrica: z.string(),
  atualRotulo: z.string(),
  metaRotulo: z.string(),
  baseRotulo: z.string(),
  volumeRotulo: z.string(),
  situacao: situacaoSchema,
  causa: z.string(),
  quemAge: z.string(),
  emJogoCentavos: centavos,
  emJogoRotulo: z.string(),
  /** No maximo 3 linhas por conta (clinica + plataforma), as de maior reais em jogo. */
  destaque: z.boolean(),
});
export type LinhaDaFila = z.infer<typeof linhaDaFilaSchema>;

export const visaoGeralSchema = z.object({
  atualizadoEm: z.string(),
  periodoRotulo: z.string(),
  resumo: resumoDaSemanaSchema,
  agirHoje: z.array(itemAgirHojeSchema),
  fila: z.array(linhaDaFilaSchema),
});
export type VisaoGeral = z.infer<typeof visaoGeralSchema>;

export const pontoDaSerieSchema = z.object({
  rotulo: z.string(),
  valor: z.number(),
});
export type PontoDaSerie = z.infer<typeof pontoDaSerieSchema>;

export const etapaDaPioraSchema = z.object({
  etapa: z.string(),
  baseRotulo: z.string(),
  atualRotulo: z.string(),
  variacaoRotulo: z.string(),
  parte: z.number().min(0).max(1),
});
export type EtapaDaPiora = z.infer<typeof etapaDaPioraSchema>;

export const anuncioDaContaSchema = z.object({
  nome: z.string(),
  gastoCentavos: centavos,
  ctrRotulo: z.string(),
  cplRotulo: z.string(),
  agendamentos: inteiro,
  sinal: z.string(),
});
export type AnuncioDaConta = z.infer<typeof anuncioDaContaSchema>;

export const alteracaoDaContaSchema = z.object({
  data: z.string(),
  classificacao: z.string(),
  descricao: z.string(),
  aprendizadoAte: z.string().nullable(),
  /** Iguais em sequência, omitido quando é 1. */
  repeticoes: z.number().int().positive().optional(),
});
export type AlteracaoDaConta = z.infer<typeof alteracaoDaContaSchema>;

/** `percentual` e a passagem da etapa anterior para esta. A primeira etapa e `null`. */
export const etapaDaSafraSchema = z.object({
  nome: z.string(),
  quantidade: inteiro,
  percentual: z.number().nullable(),
});
export type EtapaDaSafra = z.infer<typeof etapaDaSafraSchema>;

export const metasDaContaSchema = z.object({
  cplCentavos: centavos,
  tetoCentavos: centavos,
  cpmMaximoCentavos: centavos,
  ctrMinimo: z.number(),
  frequenciaMaxima: z.number(),
  saldoMinimoDias: inteiro,
  /** Visualizacoes de 3 s ÷ impressoes. A tela da central cadastra o minimo. */
  hookRateMinimo: z.number().nonnegative(),
});
export type MetasDaConta = z.infer<typeof metasDaContaSchema>;

/** Uma linha do comparativo mensal da camada de negócio (Kommo). */
export const periodoDoNegocioSchema = z.object({
  rotulo: z.string(),
  investimentoCentavos: centavos,
  custoPorAgendamentoCentavos: centavos.nullable(),
  custoPorPacienteCentavos: centavos.nullable(),
  /** Receita das vendas da safra ÷ investimento. Null sem venda mapeada ou sem gasto. */
  roas: z.number().nonnegative().nullable(),
});
export type PeriodoDoNegocio = z.infer<typeof periodoDoNegocioSchema>;

export const detalheDaContaSchema = z.object({
  clienteId: z.string().min(1),
  cliente: z.string(),
  plataforma: z.enum(["meta", "google"]),
  contaId: z.string().min(1),
  tipoCodigo: z.string().min(1),
  /** Tipos da plataforma da campanha, para a Monte corrigir o que veio do objetivo. */
  tiposDisponiveis: z.array(z.object({ codigo: z.string(), nome: z.string() })),
  /** Round-trip da coluna obrigatoria. A tela de metas nao edita este campo. */
  verbaDiariaCentavos: centavos,
  situacao: situacaoSchema,
  emJogoCentavos: centavos,
  campanhas: z.array(
    z.object({
      /** Texto da aba: tipo · métrica. Sem `campanha`, o rótulo ainda é o nome na URL. */
      rotulo: z.string(),
      /** Nome da campanha que a aba abre. Ausente nas respostas antigas. */
      campanha: z.string().optional(),
      /** Tipo da aba. A URL usa este código para não misturar campanhas homônimas. */
      tipoCodigo: z.string().optional(),
    }),
  ),
  serie: z.array(pontoDaSerieSchema),
  metaRotulo: z.string(),
  baseRotulo: z.string(),
  atualRotulo: z.string(),
  variacaoMetaRotulo: z.string(),
  variacaoBaseRotulo: z.string(),
  volumeRotulo: z.string(),
  gastoCentavos: centavos,
  piora: z.array(etapaDaPioraSchema),
  causa: z.string(),
  acaoTipica: z.string(),
  quemAge: z.string(),
  anuncios: z.array(anuncioDaContaSchema),
  /** Número cru da meta e da base, para o gráfico. Ausente nas respostas antigas. */
  metaValor: z.number().optional(),
  baseValor: z.number().optional(),
  /** Média do índice de qualidade da campanha na janela. Null fora de pesquisa. */
  indiceQualidade: z.number().nonnegative().nullable().optional(),
  palavrasChave: z
    .array(
      z.object({
        texto: z.string(),
        indiceQualidade: z.number().nullable(),
        impressoes: inteiro,
      }),
    )
    .optional(),
  alteracoes: z.array(alteracaoDaContaSchema),
  safra: z.array(etapaDaSafraSchema),
  investimentoSafraCentavos: centavos,
  custoPorAgendamentoCentavos: centavos.nullable(),
  custoPorPacienteCentavos: centavos.nullable(),
  roas: z.number().nonnegative().nullable(),
  /** Mês até hoje, mesmos dias do mês anterior, último mês fechado e o mesmo mês do ano anterior. */
  periodosDoNegocio: z.array(periodoDoNegocioSchema),
  metas: metasDaContaSchema,
});
export type DetalheDaConta = z.infer<typeof detalheDaContaSchema>;

/** Leitura da IA de uma campanha. A IA so sugere; nada muda na conta sem o gestor. */
export const leituraDaIaSchema = z.object({
  resumo: z.string().min(1),
  acoes: z.array(z.string().min(1)).max(5),
  modelo: z.string(),
  geradaEm: z.iso.datetime(),
});
export type LeituraDaIa = z.infer<typeof leituraDaIaSchema>;

export const leituraDaContaSchema = z.object({ leitura: leituraDaIaSchema.nullable() });
export type LeituraDaConta = z.infer<typeof leituraDaContaSchema>;

/** Ultimos acessos de quem abriu a Central. `abertoEm` e ISO-8601. `nome` e o rotulo da equipe, nunca o uuid. */
export const acessoDaCentralSchema = z.object({
  usuarioId: z.string(),
  nome: z.string(),
  abertoEm: z.string(),
});
export type AcessoDaCentral = z.infer<typeof acessoDaCentralSchema>;

export const acessosDaCentralSchema = z.object({
  acessos: z.array(acessoDaCentralSchema).max(50),
});
export type AcessosDaCentral = z.infer<typeof acessosDaCentralSchema>;

/** Lista inteira de quem pode alterar conta de anuncio. Vazia nao dispara guardrail. */
export const autoresAutorizadosSchema = z
  .object({
    autores: z.array(z.string().trim().min(1).max(200)).max(500),
  })
  .strict();
export type AutoresAutorizados = z.infer<typeof autoresAutorizadosSchema>;

export const resumoDoDiaSchema = z.object({
  geradoEm: z.string(),
  agirHoje: z.array(itemAgirHojeSchema),
  fila: z.array(linhaDaFilaSchema),
});
export type ResumoDoDia = z.infer<typeof resumoDoDiaSchema>;
